/* Report an issue — post-delivery reporting from a shipped build.
   Opened by the "Report an issue" button on a passed/complete build so devs can
   log a problem a client surfaces months later. Reporter is auto-captured;
   build + client are pre-filled. Funnels through MAV.reportIssue (source:
   'post-delivery') so it lands in Admin › Issues exactly like any other ticket. */

const { useState: useRiState } = React;

const RI_SEV = ['low', 'medium', 'high', 'blocker'];
const RI_SEV_LBL = { low: 'Low', medium: 'Medium', high: 'High', blocker: 'Blocker' };

function RiAvatar({ m, size = 30 }) {
  if (!m) return <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.4, background: 'var(--queued)' }}>·</span>;
  return <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.4, background: m.color }}>{m.initials}</span>;
}

function ReportIssue({ job, onClose, onOpenIssue }) {
  const MAV = window.MAV;
  // Reporter = the LIVE signed-in user (MAV.currentUser, from /me), not the mock
  // 'jd' that MAV.me() closes over. Prefer the full live roster row (carries a
  // color for the avatar) via memberById(ext_id); else currentUser + a default
  // color; else the mock (offline/demo). The submit path (below) already keys on
  // MAV.currentUser.ext_id — this aligns the DISPLAYED reporter card with it.
  const _cu = MAV.currentUser;
  const me = _cu
    ? (MAV.memberById(_cu.ext_id) || { ..._cu, color: _cu.color || 'var(--brand-amber)' })
    : (MAV.me ? MAV.me() : MAV.memberById('jd'));
  const client = job ? MAV.clientBySlug(job.client) : null;
  const [type, setType] = useRiState('Bug');
  const [sev, setSev] = useRiState('high');
  const [title, setTitle] = useRiState('');
  const [stage, setStage] = useRiState('');
  const [text, setText] = useRiState('');
  const [labels, setLabels] = useRiState('');
  const [result, setResult] = useRiState(null);

  const canSubmit = title.trim() && text.trim();
  const submit = async () => {
    const payload = {
      type, sev, title: title.trim(), text: text.trim(),
      build: job ? job.name : '', stage: stage || '',
      labels: ['post-delivery', ...labels.split(',').map(s => s.trim()).filter(Boolean)],
      source: 'post-delivery',
    };
    // Live backend (Jira-lite) → persists; falls back to the in-memory mock offline.
    if (window.GooseAPI && window.GooseAPI.createIssue) {
      try {
        const r = await window.GooseAPI.createIssue(payload);
        if (r && r.issue) {
          if (Array.isArray(MAV.issues)) MAV.issues.unshift(r.issue); // visible without a reload
          setResult(r.issue);
          return;
        }
      } catch (e) { /* network/error → mock fallback below */ }
    }
    setResult(MAV.reportIssue({ reporter: (MAV.currentUser && MAV.currentUser.ext_id) || MAV.currentUser || 'jd', ...payload }));
  };

  if (result) {
    return ReactDOM.createPortal(
      <div className="fb-overlay" onClick={onClose}>
        <div className="fb-modal fb-done" onClick={e => e.stopPropagation()}>
          <div className="fb-done-ico"><I.check size={28} /></div>
          <h2 className="fb-done-h">Reported · {result.id}</h2>
          <p className="fb-done-p"><b>{result.title}</b> is filed against <b>{job ? job.name : 'this build'}</b> and routed to <b>triage</b> in Admin › Issues.</p>
          <div className="fb-done-acts">
            <button className="btn btn-ghost" onClick={() => { setResult(null); setTitle(''); setText(''); setLabels(''); }}>Report another</button>
            <button className="btn btn-primary" onClick={() => { onClose(); if (onOpenIssue) onOpenIssue(result.id); }}>Open ticket <I.arrow size={15} /></button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="fb-head">
          <div>
            <div className="row gap-8" style={{ marginBottom: 7 }}>
              <span className="chip" style={{ background: 'var(--brand-yellow)', color: 'var(--ink-on-brand)', fontWeight: 700 }}>Report</span>
              <span className="chip">Post-delivery</span>
            </div>
            <h2 className="fb-title">Report an issue</h2>
            <p className="fb-sub">For problems found on a shipped build — even months later.</p>
          </div>
          <button className="icon-act" onClick={onClose} title="Close"><I.x size={16} /></button>
        </div>

        <div className="fb-body">
          {/* build context — locked */}
          {job && (
            <div className="ri-context">
              <ClientBadge slug={job.client} size={30} />
              <div style={{ minWidth: 0 }}>
                <div className="mono" style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.name}</div>
                <div className="muted" style={{ fontSize: 11 }}>{client ? client.name : ''} · {job.id} · shipped {job.started}</div>
              </div>
            </div>
          )}

          {/* reporter */}
          <div className="ri-reporter">
            <div className="row gap-10" style={{ alignItems: 'center' }}>
              <RiAvatar m={me} size={28} />
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 700 }}>{me ? me.name : 'You'} <span className="muted" style={{ fontWeight: 500 }}>· reporter</span></div>
                <div className="muted" style={{ fontSize: 10.5 }}>Auto-captured · immutable after filing</div>
              </div>
            </div>
          </div>

          {/* type + severity */}
          <div className="fb-section" style={{ paddingTop: 4 }}>
            <span className="fb-q">Type</span>
            <div className="fb-seg" style={{ gridTemplateColumns: '1fr 1fr' }}>
              {['Bug', 'Feature request'].map(t => (
                <button key={t} type="button" className={'fb-seg-btn' + (type === t ? ' active' : '')} onClick={() => setType(t)}>
                  {t === 'Bug' ? <I.flame size={14} /> : <I.star size={14} />}{t}
                </button>
              ))}
            </div>
          </div>
          <div className="fb-section">
            <span className="fb-q">Severity</span>
            <div className="fb-sev">
              {RI_SEV.map(s => (
                <button key={s} type="button" className={'fb-sev-btn sev-' + s + (sev === s ? ' on' : '')} onClick={() => setSev(s)}>{RI_SEV_LBL[s]}</button>
              ))}
            </div>
          </div>

          {/* title */}
          <div className="ep-field" style={{ marginTop: 4 }}>
            <label className="field-lbl">Title</label>
            <input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Dark-mode logo broke after client's ESP migration" autoFocus />
          </div>

          {/* stage */}
          <div className="ep-field">
            <label className="field-lbl">Likely stage <span className="muted" style={{ fontWeight: 500 }}>· optional</span></label>
            <select className="input" value={stage} onChange={e => setStage(e.target.value)}>
              <option value="">Not sure / not stage-specific</option>
              {MAV.stageDefs.map(s => <option key={s.id} value={s.id}>{s.no} · {s.name}</option>)}
            </select>
          </div>

          {/* description */}
          <div className="ep-field">
            <label className="field-lbl">What's wrong &amp; how it surfaced</label>
            <textarea className="input" rows={3} value={text} onChange={e => setText(e.target.value)} placeholder="What the client reported, where it renders wrong, the ESP/client, any links or screenshots…" style={{ resize: 'none' }} />
          </div>

          {/* labels */}
          <div className="ep-field" style={{ marginBottom: 0 }}>
            <label className="field-lbl">Labels <span className="muted" style={{ fontWeight: 500 }}>· optional, comma-separated</span></label>
            <input className="input" value={labels} onChange={e => setLabels(e.target.value)} placeholder="dark-mode, outlook, regression" />
            <p className="muted" style={{ fontSize: 11, marginTop: 7 }}>Auto-tagged <b>post-delivery</b> so it's filterable in the Issues directory.</p>
          </div>
        </div>

        <div className="fb-foot">
          <span className="muted" style={{ fontSize: 11.5 }}>Lands in triage · {RI_SEV_LBL[sev]} SLA</span>
          <div className="row gap-10">
            <button className="btn btn-quiet" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-lg" onClick={submit} disabled={!canSubmit} title={canSubmit ? 'File the report' : 'Title and description are required'}><I.send size={15} /> Submit report</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

window.ReportIssue = ReportIssue;
